import { Button, TextButton } from '@creditornot/cb-button';
import { FormApi } from 'final-form';
import arrayMutators from 'final-form-arrays';
import React, { useCallback, useMemo, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { APIErrorNotification } from 'common/components';
import validateYupSchema from 'common/utils/validateYupSchema';
import { getModifiedFieldsForTracking } from 'modules/menu/hooks/telemetryHooks';
import StickyFooter from 'modules/menu/items/components/Item/ItemDetails/StickyFooter';

import { AddOptionValueButton } from './AddOptionValueButton';
import { OfferLinks } from './OfferLinks';
import { OptionFormFields } from './OptionFormFields';
import { OptionValueAdvancedDetailsModal } from './OptionValueAdvancedDetailsModal';
import { UnpurchasableOfferWarning } from './UnpurchasableOfferWarning';
import { OptionTab, optionTabs } from './constants';
import { OptionFormData } from './types';
import { formValuesToUpdateOptionPayload } from './utils';
import { validationSchema } from './validationSchema';
import { useSelectionData } from '../../../../hooks/useSelectionData';
import { Option } from '../../types';
import { getOffersThatWillBecomeUnpurchasable, simulateOptionUpdateApiCall } from '../../unpurchasabilityUtils';
import { TabbedSection, TabsContainer, Root, StyledTabs } from '../CommonPanelStyles';
import { TabCountIcon } from '../TabCountIcon';

type Props = {
  initialValues: OptionFormData;
  activeOption?: Option;
  onCancel: () => void;
  onSubmit: (formValues: OptionFormData, modifiedFields: string[]) => void;
  error: any;
  submitting: boolean;
  mode: 'create' | 'edit';
};

const Content = styled.div`
  padding: 0 24px;
`;

const OptionValueActions = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  margin-block-start: 12px;
  margin-block-end: 12px;
`;

export const OptionForm = ({ onCancel, initialValues, onSubmit, error, submitting, mode, activeOption }: Props) => {
  const { selection, offers, options } = useSelectionData();
  const [activeTabId, setActiveTabId] = useState<OptionTab>(optionTabs.DETAILS);
  const intl = useIntl();

  const [advancedDetailsModalIsOpen, setAdvancedDetailsModalIsOpen] = useState(false);
  const handleAdvancedDetailsModalClose = useCallback(() => setAdvancedDetailsModalIsOpen(false), []);

  const offersUsingCurrentOption = useMemo(
    () =>
      activeOption ? offers.filter(offer => !!offer.option_bindings?.find(ob => ob.option_id === activeOption.id)) : [],
    [activeOption, offers],
  );

  const getNewUnpurchasableOffers = useCallback(
    (values: OptionFormData) => {
      if (!activeOption) {
        return null;
      }
      const optionUpdatePayload = formValuesToUpdateOptionPayload(selection.primary_language, activeOption, values);
      const simulatedOption = simulateOptionUpdateApiCall(activeOption, optionUpdatePayload);
      return getOffersThatWillBecomeUnpurchasable(simulatedOption, offersUsingCurrentOption, options);
    },
    [activeOption, offersUsingCurrentOption, options, selection.primary_language],
  );

  return (
    <>
      <TabsContainer>
        <StyledTabs
          activeTabId={activeTabId}
          items={[
            {
              id: optionTabs.DETAILS,
              label: intl.formatMessage({ id: 'shared-menu.editor.option-tabs.details' }),
            },
            {
              id: optionTabs.ITEMS,
              label: intl.formatMessage({ id: 'shared-menu.editor.option-tabs.items' }),
              rightContent: (
                <TabCountIcon
                  count={offersUsingCurrentOption.length}
                  variant={activeTabId === optionTabs.ITEMS ? 'active' : 'info'}
                />
              ),
            },
          ]}
          onChange={(id: OptionTab) => setActiveTabId(id)}
        />
      </TabsContainer>
      <Root>
        <TabbedSection $isShown={activeTabId === optionTabs.DETAILS}>
          <Form
            initialValues={initialValues}
            onSubmit={(formData, form: FormApi<OptionFormData, Partial<OptionFormData>>) => {
              const modifiedFields = getModifiedFieldsForTracking<OptionFormData>(form.getState());
              onSubmit(formData, modifiedFields);
            }}
            mutators={{ ...arrayMutators }}
            validate={validateYupSchema(validationSchema)}
            keepDirtyOnReinitialize
          >
            {({ handleSubmit, hasValidationErrors, valid, values }) => {
              const offersThatWillBecomeUnpurchasable = valid ? getNewUnpurchasableOffers(values) : null;
              return (
                <form onSubmit={handleSubmit}>
                  <Content>
                    {/* We are gradually sunsetting Switch options so hide the Switch option type in the new option creation modal */}
                    <OptionFormFields
                      allowChangingOptionType={mode === 'create'}
                      hideSwitchOptionType={mode === 'create'}
                    />
                    <UnpurchasableOfferWarning
                      isMultiChoice={activeOption?.type === 'multi_choice'}
                      offers={offersThatWillBecomeUnpurchasable}
                      language={selection.primary_language}
                    />
                    <OptionValueActions>
                      <Field<OptionFormData['type']> name="type">
                        {({ input: { value } }) =>
                          (value === 'choice' || value === 'multi_choice') && <AddOptionValueButton />
                        }
                      </Field>
                      <TextButton
                        type="button"
                        onClick={() => {
                          setAdvancedDetailsModalIsOpen(true);
                        }}
                      >
                        <FormattedMessage id="menu-option.edit-advanced-details" />
                      </TextButton>
                    </OptionValueActions>
                    {error && <APIErrorNotification error={error} style={{ marginBlockEnd: '16px' }} />}
                  </Content>
                  <StickyFooter>
                    <Button variant="secondary" size="small" onClick={onCancel} style={{ marginInlineEnd: '16px' }}>
                      <FormattedMessage id="state.cancel" />
                    </Button>
                    <Button
                      type="submit"
                      disabled={hasValidationErrors || submitting}
                      loading={submitting}
                      variant="primary"
                      size="small"
                    >
                      {mode === 'create' ? (
                        <FormattedMessage id="menu-option.add-option-submit" />
                      ) : (
                        <FormattedMessage id="state.save" />
                      )}
                    </Button>
                  </StickyFooter>
                  <OptionValueAdvancedDetailsModal
                    isOpen={advancedDetailsModalIsOpen}
                    onClose={handleAdvancedDetailsModalClose}
                  />
                </form>
              );
            }}
          </Form>
        </TabbedSection>
        <TabbedSection $isShown={activeTabId === optionTabs.ITEMS}>
          <OfferLinks activeOption={activeOption} offersUsingCurrentOption={offersUsingCurrentOption} />
        </TabbedSection>
      </Root>
    </>
  );
};
